import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box, IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CircleTableRow, { type CircleRowData } from "./CircleTableRow";

interface SortableCirclesListProps {
  circles: CircleRowData[];
  isMobile: boolean;
  canReorder: boolean;
  onOpen: (circleId: string) => void;
  onReorder: (orderedSubcircleIds: string[]) => Promise<void>;
}

interface SortableCircleRowProps {
  circle: CircleRowData;
  isMobile: boolean;
  onOpen: (circleId: string) => void;
}

const SortableCircleRow: React.FC<SortableCircleRowProps> = ({
  circle,
  isMobile,
  onOpen,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: circle.circleId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandle = (
    <IconButton
      size="small"
      {...attributes}
      {...listeners}
      onClick={(event) => event.stopPropagation()}
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
        color: "text.secondary",
        touchAction: "none",
      }}
      aria-label={`Reorder ${circle.name}`}
    >
      <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.55 }} />
    </IconButton>
  );

  return (
    <Box ref={setNodeRef} style={style}>
      <CircleTableRow
        circle={circle}
        isMobile={isMobile}
        dragHandle={dragHandle}
        isDragging={isDragging}
        onOpen={onOpen}
      />
    </Box>
  );
};

const SortableCirclesList: React.FC<SortableCirclesListProps> = ({
  circles,
  isMobile,
  canReorder,
  onOpen,
  onReorder,
}) => {
  const [items, setItems] = useState(circles);

  useEffect(() => {
    setItems(circles);
  }, [circles]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const mainCircle = useMemo(
    () => items.find((circle) => !circle.isSubCircle) ?? null,
    [items]
  );
  const subCircles = useMemo(
    () => items.filter((circle) => circle.isSubCircle),
    [items]
  );
  const sortableIds = useMemo(
    () => subCircles.map((circle) => circle.circleId),
    [subCircles]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = subCircles.findIndex(
        (circle) => circle.circleId === active.id
      );
      const newIndex = subCircles.findIndex(
        (circle) => circle.circleId === over.id
      );
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      const reorderedSubCircles = arrayMove(subCircles, oldIndex, newIndex);
      const nextItems = mainCircle
        ? [mainCircle, ...reorderedSubCircles]
        : reorderedSubCircles;
      const previousItems = items;

      setItems(nextItems);

      try {
        await onReorder(reorderedSubCircles.map((circle) => circle.circleId));
      } catch {
        setItems(previousItems);
      }
    },
    [items, mainCircle, onReorder, subCircles]
  );

  if (!canReorder) {
    return (
      <>
        {items.map((circle) => (
          <CircleTableRow
            key={circle.circleId}
            circle={circle}
            isMobile={isMobile}
            onOpen={onOpen}
          />
        ))}
      </>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      {mainCircle && (
        <CircleTableRow
          key={mainCircle.circleId}
          circle={mainCircle}
          isMobile={isMobile}
          onOpen={onOpen}
        />
      )}
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {subCircles.map((circle) => (
          <SortableCircleRow
            key={circle.circleId}
            circle={circle}
            isMobile={isMobile}
            onOpen={onOpen}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableCirclesList;
